import { Component, DestroyRef, inject, Inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  Validators,
  AbstractControl,
  ValidatorFn,
} from '@angular/forms';
import { CoreService } from '@app/@core/services/core.service';
import { finalize, take } from 'rxjs/operators';
import { AuthenticationService } from '../authentication.service';
import { ActivatedRoute, Router } from '@angular/router';
import { LanguageService } from '@app/@core/services/language.service';
import { I18nService } from '@app/i18n';
import { interval, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { emailRegex } from '@app/@core/services/auth.service';
import { ContactInfo } from '@app/contact/contact.types';
import { ContactService } from '@app/contact/contact.service';

@Component({
  selector: 'app-forgot-password',
  templateUrl: './forgot-password.component.html',
  styleUrls: ['./forgot-password.component.scss'],
  standalone: false,
})
export class ForgotPasswordComponent implements OnInit {
  emailForm: FormGroup;
  verificationCodeForm: FormGroup;
  newPasswordForm: FormGroup = new FormGroup({});
  loading = {
    sending: false,
    verifying: false,
    resetting: false,
  };
  step: number = 1;
  dir: string;
  currentLanguage: string;
  passwordResetToken: string;
  isTimerActive = false;
  showPassword = false;
  showConfirmPassword = false;
  timer: number;
  timerSubscription: Subscription;
  contactInfo: ContactInfo;
  otpConfig = { length: 6, allowNumbersOnly: true };
  constructor(
    public router: Router,
    private route: ActivatedRoute,
    private authService: AuthenticationService,
    private coreService: CoreService,
    private languageService: LanguageService,
    private i18nService: I18nService,
    private translateService: TranslateService,
    private contactService: ContactService,
  ) {
    this.currentLanguage = this.i18nService.language;
    this.setLanguage();
  }

  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.dir = localStorage.getItem('html-dir');
    this.setupForms();
    this.getSupportContact();
  }

  getSupportContact() {
    this.contactService
      .getContactsInfo()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (res: ContactInfo) => {
          this.contactInfo = res;
        },
        (err) => {},
      );
  }

  changeLanguage(currentLanguage: string) {
    let targetedLanguage = currentLanguage;
    this.i18nService.language = targetedLanguage;
    this.languageService.language = targetedLanguage;
    this.languageService.changeHtmlDirection(targetedLanguage);
    location.reload();
  }

  setLanguage() {
    if (
      this.route.snapshot.queryParams['lang'] == 'ar' &&
      this.currentLanguage != 'ar'
    ) {
      this.i18nService.language = 'ar';
      this.languageService.language = 'ar';
      this.languageService.changeHtmlDirection('ar');
    } else if (
      this.route.snapshot.queryParams['lang'] == 'en' &&
      this.currentLanguage != 'en'
    ) {
      this.i18nService.language = 'en';
      this.languageService.language = 'en';
      this.languageService.changeHtmlDirection('en');
    }
  }

  setupForms() {
    this.emailForm = new FormGroup({
      email: new FormControl('', [
        Validators.required,
        Validators.pattern(emailRegex),
      ]),
    });
    this.verificationCodeForm = new FormGroup({
      code: new FormControl('', [
        Validators.required,
        Validators.minLength(6),
        Validators.maxLength(6),
      ]),
    });
    this.newPasswordForm = new FormGroup({
      newPassword: new FormControl('', [
        Validators.required,
        Validators.pattern(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/,
        ),
      ]),
      confirmPassword: new FormControl('', [
        Validators.required,
        this.matchOtherValidator('newPassword'),
      ]),
    });

    this.newPasswordForm.controls.newPassword.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() =>
        this.newPasswordForm.controls.confirmPassword.updateValueAndValidity(),
      );
  }

  sendVerifyCode() {
    if (this.emailForm.controls.email.invalid) return;
    this.loading.sending = true;
    const body = {
      email: this.emailForm.get('email').value,
    };
    this.authService
      .sendVerification(body)
      .pipe(
        finalize(() => {
          this.loading.sending = false;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(
        (response) => {
          this.step = 2;

          const expiryTimeInMilliSeconds = response?.expiryTime - Date.now(); // Extract the expiry time from the response
          let expiryTimeInSeconds = 60;
          if (expiryTimeInMilliSeconds > 0) {
            expiryTimeInSeconds = Math.floor(expiryTimeInMilliSeconds / 1000);
          }
          this.startTimer(expiryTimeInSeconds);
        },
        (err) => this.coreService.showErrorDialog(err),
      );
  }

  private matchOtherValidator(otherControlName: string): ValidatorFn {
    return (control: AbstractControl) => {
      if (!control.parent) {
        return null; // we don’t have a parent yet
      }
      const otherControl = control.parent.get(otherControlName);
      if (!otherControl) {
        return null;
      }

      if (otherControl.value !== control.value) {
        return { mismatch: true };
      }
      return null;
    };
  }

  verifyCode() {
    if (this.verificationCodeForm.controls.code.invalid) return;
    this.loading.verifying = true;
    const body = {
      email: this.emailForm.get('email').value,
      code: this.verificationCodeForm.get('code').value,
    };
    this.authService
      .verifyEmail(body)
      .pipe(
        finalize(() => {
          this.loading.verifying = false;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(
        (res) => {
          this.passwordResetToken = res.token;
          this.step = 3;
        },
        (err) => this.coreService.showErrorDialog(err?.error?.message || err),
      );
  }

  resetPassword() {
    if (this.newPasswordForm.controls.newPassword.invalid) return;
    this.loading.resetting = true;
    const body = {
      password: this.newPasswordForm.get('newPassword').value,
    };
    this.authService
      .resetPassword(body, this.passwordResetToken)
      .pipe(
        finalize(() => {
          this.loading.resetting = false;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(
        (res) => {
          this.passwordResetToken = null;
          this.coreService.showSuccessDialog(
            'Success',
            'Password reset successfully',
            null,
            null,
            'Ok',
            { redirectUrl: '/login' },
          );
        },
        (err) => this.coreService.showErrorDialog(err),
      );
  }

  startTimer(duration: number) {
    this.isTimerActive = true;
    this.timer = duration;
    this.timerSubscription = interval(1000).subscribe(() => {
      this.timer--;
      if (this.timer === 0) {
        this.stopTimer();
      }
    });
  }

  getTimerText(sec: number) {
    if (sec > 60) {
      const minutes = Math.floor(sec / 60);
      const seconds = sec % 60;
      return `${minutes} ${this.translateService.instant(
        'minute(s)',
      )} ${this.translateService.instant(
        'and',
      )} ${seconds} ${this.translateService.instant('second(s)')}`;
    }
    return `${sec} ${this.translateService.instant('second(s)')}`;
  }

  stopTimer() {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
    this.isTimerActive = false;
  }

  showHidePassword() {
    this.showPassword = !this.showPassword;
  }
  showHideConfirmPassword() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  goBack() {
    this.step = 1;
    this.verificationCodeForm.reset();
  }
}
